import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles, TrendingUp, AlertCircle } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';

interface FacialAnalysisProps {
  isPremium: boolean;
  facialFeatures: any;
}

const FacialAnalysis: React.FC<FacialAnalysisProps> = ({ isPremium, facialFeatures }) => {
  const getDescription = (score: number) => {
    if (score >= 9.0) return "Exceptional - top tier";
    if (score >= 8.0) return "Above average - strong feature";
    if (score >= 7.0) return "Good - attractive feature";
    if (score >= 6.0) return "Average - well-proportioned";
    return "Room for improvement";
  };

  const features = [
    { feature: "Facial Symmetry", score: facialFeatures.symmetry, description: getDescription(facialFeatures.symmetry) },
    { feature: "Jawline Definition", score: facialFeatures.jawline, description: getDescription(facialFeatures.jawline) },
    { feature: "Eye Area", score: facialFeatures.eyeArea, description: getDescription(facialFeatures.eyeArea) },
    { feature: "Nose Proportion", score: facialFeatures.nose, description: getDescription(facialFeatures.nose) },
    { feature: "Skin Quality", score: facialFeatures.skinQuality, description: getDescription(facialFeatures.skinQuality) },
    { feature: "Smile Appeal", score: facialFeatures.smile, description: getDescription(facialFeatures.smile) },
  ];

  // Find best feature
  const bestFeature = features.reduce((prev, current) => 
    (prev.score > current.score) ? prev : current
  );

  const improvements = [
    {
      area: "Grooming",
      priority: "High",
      tips: [
        "Maintain consistent skincare routine",
        "Consider professional eyebrow grooming",
        "Keep facial hair well-trimmed and shaped"
      ]
    },
    {
      area: "Style",
      priority: "Medium",
      tips: [
        "Experiment with hairstyles that complement face shape",
        "Choose clothing colors that enhance skin tone",
        "Invest in quality accessories"
      ]
    },
    {
      area: "Photography",
      priority: "High",
      tips: [
        "Use natural lighting for photos",
        "Angle camera slightly above eye level",
        `Show ${bestFeature.feature.toLowerCase()} - your best feature!`
      ]
    }
  ];

  if (!isPremium) {
    return null;
  }

  return (
    <div className="space-y-6">
      <Card className="p-6 bg-gradient-to-br from-blue-50 to-purple-50">
        <h4 className="font-semibold text-lg mb-4 flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-purple-600" />
          Detailed Facial Analysis
        </h4>
        
        <div className="space-y-4">
          {features.map((feature, index) => (
            <motion.div
              key={feature.feature}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <div className="flex items-center justify-between mb-2">
                <div>
                  <span className="font-medium text-gray-800">{feature.feature}</span>
                  <p className="text-xs text-gray-600">{feature.description}</p>
                </div>
                <span className="text-lg font-bold text-purple-600">{feature.score}/10</span>
              </div>
              <Progress value={feature.score * 10} className="h-2" />
            </motion.div>
          ))}
        </div>
      </Card>

      <Card className="p-6 bg-white">
        <h4 className="font-semibold text-lg mb-4 flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-green-600" />
          Personalized Improvement Plan
        </h4>
        
        <div className="space-y-4">
          {improvements.map((improvement, index) => (
            <motion.div
              key={improvement.area}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="border-l-4 border-purple-500 pl-4 py-2"
            >
              <div className="flex items-center gap-2 mb-2">
                <h5 className="font-semibold text-gray-800">{improvement.area}</h5>
                <span className={`text-xs px-2 py-1 rounded-full ${
                  improvement.priority === 'High' 
                    ? 'bg-red-100 text-red-700' 
                    : 'bg-yellow-100 text-yellow-700'
                }`}>
                  {improvement.priority} Priority
                </span>
              </div>
              <ul className="space-y-1">
                {improvement.tips.map((tip, i) => (
                  <li key={i} className="text-sm text-gray-700 flex items-start gap-2">
                    <span className="text-purple-600">•</span>
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </Card>

      <Card className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
        <div className="flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-green-600 mt-0.5" />
          <div>
            <h5 className="font-semibold text-green-900 mb-1">Pro Tip</h5>
            <p className="text-sm text-green-800">
              Your {bestFeature.feature.toLowerCase()} is your strongest feature ({bestFeature.score}/10)! Make sure to highlight it in your dating profile photos. 
              Studies show emphasizing your best features can increase match rates by up to 76%.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default FacialAnalysis;